import { useEffect, useRef, type ReactNode } from "react";

/**
 * Cabeçalho de página: título, subtítulo e ações da superfície.
 *
 * O título recebe `tabIndex={-1}` e o foco na montagem porque mudança de rota
 * DEVE posicionar o foco no início significativo da nova superfície
 * (AGENTS §17). Só o bloco de título anima; a página inteira nunca recebe fade
 * (§15).
 */
export interface PageHeadProps {
  title: string;
  /** Linha de contexto: contagens, período, denominador. */
  sub?: ReactNode;
  actions?: ReactNode;
  /** Falso quando o cabeçalho não inicia uma rota (painel dentro da página). */
  focusOnMount?: boolean;
  className?: string;
}

export function PageHead({
  title,
  sub,
  actions,
  focusOnMount = true,
  className,
}: PageHeadProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!focusOnMount) return;
    titleRef.current?.focus();
  }, [focusOnMount]);

  return (
    <header className={className ? `cx-pagehead ${className}` : "cx-pagehead"}>
      <div>
        <h1 ref={titleRef} tabIndex={-1}>
          {title}
        </h1>
        {sub && <p className="cx-sub">{sub}</p>}
      </div>
      {actions && <div className="cx-pagehead-actions">{actions}</div>}
    </header>
  );
}

/** Barra de controles logo abaixo do cabeçalho: abas, filtros e busca. */
export function Toolbar({
  children,
  className,
  label,
}: {
  children: ReactNode;
  className?: string;
  /** Nome acessível quando a barra agrupa controles sem título próprio. */
  label?: string;
}) {
  return (
    <div
      className={className ? `cx-bar ${className}` : "cx-bar"}
      role={label ? "group" : undefined}
      aria-label={label}
    >
      {children}
    </div>
  );
}

/** Espaçador flexível da barra; empurra o que vem depois para a direita. */
export function ToolbarSpacer() {
  return <span className="cx-bar-spacer" aria-hidden="true" />;
}
